import View16 from '@carbon/icons-react/es/view/16';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Page } from 'components/molecules/page';
import { getDynamicTemplatePreviewAction } from 'data/templates/projects/actions';
import { getDynamicFieldsForPreview } from 'data/templates/projects/api/transform';
import { useActiveProjectTemplateStore } from 'data/templates/projects/hooks/use-active-project-template';
import type { ProjectTemplateProjectPhase } from 'data/templates/projects/types';
import { useProjectFieldsQuery } from 'queries';
import { toFieldsValues } from 'routes/projects/create/utils';
import { ChangePreviewDrawer } from './change-preview-drawer';
import { ProjectTemplatePreviewGantt } from './preview-timeline';

const StyledTemplateName = styled.span`
  margin-left: var(--spacing-02);
`;

const TitleWrapper = styled.span`
  flex: 1;
`;

const Header = styled(Page.Header)`
  && {
    padding-bottom: var(--spacing-03);

    ${Page.Header.Title} {
      width: 100vw;
    }

    ${Button} {
      margin: calc(-1 * var(--layout-02)) 0 0 var(--layout-03);
    }
  }
`;

const ChangePreviewButton = styled(Button)`
  margin-left: var(--layout-02) !important;
`;

export const ProjectDynamicTemplatePreview: FC<unknown> = () => {
  const [changePreview, setChangePreview] = useState(true);
  const activeTemplate = useActiveProjectTemplateStore();
  const dynamicFields = getDynamicFieldsForPreview(activeTemplate);
  const projectFieldsQueryResult = useProjectFieldsQuery();
  const projectFields = projectFieldsQueryResult.data
    ? projectFieldsQueryResult.data
    : [];

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [phases, setPhases] = useState(
    null as unknown as null | ProjectTemplateProjectPhase[],
  );

  const onSuccess = (payload) => {
    const {
      templateContent: { projectPhases },
    } = payload;

    const sortedProjectPhases = [...projectPhases].sort(
      (projectPhaseA, projectPhasesB) =>
        projectPhaseA.startDay - projectPhasesB.startDay,
    );

    setPhases(sortedProjectPhases);
  };

  const onFinally = () => {
    setChangePreview(false);
  };

  const onSubmit = () => {
    const formProjectFields = toFieldsValues(
      form.getFieldsValue().fields,
      projectFields,
    );
    const formProjectFieldMap = formProjectFields.reduce(
      (fieldMap, fieldObj) => {
        return {
          ...fieldMap,
          [fieldObj.fieldId]: fieldObj.fieldValue,
        };
      },
      {},
    );

    dispatch(
      getDynamicTemplatePreviewAction({
        fields: { ...form.getFieldsValue().fields, ...formProjectFieldMap },
        compressTimeline:
          // while sending payload we send it outside templatemeta, but when we receive we get it in templateMeta
          activeTemplate.template.compressTimeline ??
          activeTemplate.template.templateMeta?.compressTimeline ??
          false,
        onSuccess,
        onFinally,
      }),
    );
  };

  return (
    <Page>
      <Header
        breadcrumbs={
          <Breadcrumb.Item>
            <Link
              to={(location) => ({
                ...location,
                pathname: location.pathname.replace('/preview', ''),
              })}
            >
              <FormattedMessage id="templates.dynamic.conditions.preview.exit.link" />
            </Link>
          </Breadcrumb.Item>
        }
        title={
          <Page.Header.Title>
            <TitleWrapper>
              <FormattedMessage id="templates.dynamic.conditions.preview.header.prefix" />
              :
              <StyledTemplateName>
                {activeTemplate.template.templateName}
              </StyledTemplateName>
            </TitleWrapper>
            <Button type="link" onClick={() => setPhases(null)}>
              <FormattedMessage id="templates.dynamic.conditions.preview.reset.button" />
            </Button>
            <ChangePreviewButton
              type="primary"
              iconPlacement="right"
              icon={<View16 />}
              onClick={() => setChangePreview(true)}
            >
              <FormattedMessage id="templates.dynamic.conditions.preview.change.button" />
            </ChangePreviewButton>
          </Page.Header.Title>
        }
        subText={
          <FormattedMessage id="templates.dynamic.conditions.preview.header.caption" />
        }
      />
      <Page.Content>
        <ProjectTemplatePreviewGantt phases={phases} />
      </Page.Content>
      <ChangePreviewDrawer
        form={form}
        fields={Object.values(dynamicFields)}
        visible={changePreview}
        onSubmit={onSubmit}
        onClose={() => setChangePreview(false)}
      />
    </Page>
  );
};
